import { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction, json, redirect } from '@remix-run/node';
import { useLoaderData, useSearchParams, useSubmit } from '@remix-run/react';
import React from 'react';
import { fireScheduleTemplateSelected } from '~/analytics/segmentTrack';
import PieIcon5050Small from '~/components/icons/PieIcon-5050-Small';
import PieIcon6040Small from '~/components/icons/PieIcon-6040-Small';
import PieIcon7030Small from '~/components/icons/PieIcon-7030-Small';
import PieIcon8020Small from '~/components/icons/PieIcon-8020-Small';
import Notification from '~/components/notifications/Notification';
import Header from '~/components/route/Header';
import ScrollableTagList from '~/components/selectSchedule/ScrollableTagList';
import TagFilterMenu from '~/components/selectSchedule/TagFilterMenu';
import TemplateTypeCard from '~/components/selectSchedule/TemplateTypeCard';
import TimesplitFilters from '~/components/selectSchedule/TimesplitFilters';
import { ANALYTICS_EVENTS, CUSTOM_DAY_ASSIGNMENTS, NOTIFICATION, TEMPLATE_ID, TIMESPLIT } from '~/constants/cookies';
import { PageRoutes } from '~/constants/routes';
import { CUSTOM_TEMPLATE_ID, CUSTOM_TIMESPLIT } from '~/constants/schedule';
import CustomScheduleCard from '~/components/selectSchedule/CustomScheduleCard';
import useAnalyticsEvents from '~/hooks/useAnalyticsEvents';
import useIsLoading from '~/hooks/useIsLoading';
import { Category } from '~/interfaces/Category';
import { Tag } from '~/interfaces/Tag';
import { Template } from '~/interfaces/Template';
import { TemplateWithTimesplit } from '~/interfaces/TemplateWithTimesplit';
import { commitSession, getSession } from '~/sessions';
import { Timesplit } from '~/types/Timesplit';
import extractTimesplitFromCategoryTitle from '~/utils/extractTimesplitFromCategoryTitle';
import filterTemplatesByParams from '~/utils/filterTemplatesByParams';
import { getCategories } from '~/utils/getCategories';
import { getTags } from '~/utils/getTags';
import { getTemplates } from '~/utils/getTemplates';
import mapApiTemplates from '~/utils/mapApiTemplates';

export const meta: MetaFunction = () => {
  return [{ title: 'Pick a schedule template | Custody Navigator' }];
};

const TIMESPLIT_FILTER_PARAM = 'filter';
const TAG_FILTER_PARAM = 'tag';

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request);
  const notification = session.get(NOTIFICATION) || null;
  const analyticsEvents = session.get(ANALYTICS_EVENTS) || [];
  const response: Category[] = await getCategories();
  const url = new URL(request.url);
  const templateCategories = response.filter(({ categoryId }) => categoryId !== 5);
  const templates = await getTemplates();
  const tags: Tag[] = await getTags();
  const timesplitFilters = url.searchParams.getAll(TIMESPLIT_FILTER_PARAM);
  const tagFilters = url.searchParams.getAll(TAG_FILTER_PARAM).map(Number);
  const filteredTemplates = filterTemplatesByParams({ templates, timesplitFilters, tagFilters });
  const mappedTemplates = mapApiTemplates(
    filteredTemplates
      .filter((template: Template) => template.scheduleTemplateId !== 5 && template.scheduleTemplateId !== 6)
      .sort((a: Template, b: Template) => a.categoryId - b.categoryId)
  );
  const mappedCategories = templateCategories.map((category) => ({
    ...category,
    timesplitName: extractTimesplitFromCategoryTitle(category.name),
  }));

  return json(
    {
      analyticsEvents,
      notification,
      tags,
      templateCategories: mappedCategories,
      templates: mappedTemplates,
    },
    {
      headers: {
        'Set-Cookie': await commitSession(session),
      },
    }
  );
}

export async function action({ request }: ActionFunctionArgs) {
  const session = await getSession(request);
  const formData = await request.formData();
  const timesplitName = formData.get('timesplitName') as Timesplit;
  const templateId = formData.get('templateId') as string;
  session.set(TIMESPLIT, timesplitName);
  session.set(TEMPLATE_ID, templateId);

  // a previously drawn pattern shouldn't leak into a different template
  session.unset(CUSTOM_DAY_ASSIGNMENTS);

  return redirect(PageRoutes.PARENT_INFO, {
    headers: {
      'Set-Cookie': await commitSession(session),
    },
  });
}

export const timesplitIcons: { [key: string]: React.ReactNode } = {
  '50/50': <PieIcon5050Small />,
  '60/40': <PieIcon6040Small />,
  '70/30': <PieIcon7030Small />,
  '80/20': <PieIcon8020Small />,
};

export default function ScheduleTemplates() {
  const { analyticsEvents, notification, tags, templateCategories, templates } = useLoaderData<typeof loader>();
  const submit = useSubmit();
  const isLoading = useIsLoading();
  const [searchParams, setSearchParams] = useSearchParams();
  const timesplitFilters = searchParams.getAll(TIMESPLIT_FILTER_PARAM);
  const tagFilters = searchParams.getAll(TAG_FILTER_PARAM);
  const availableTimesplits = new Set(templates.map((template) => template.timesplitName));
  const availableTagIds = new Set(templates.flatMap((template) => template.tags.map((tag) => tag.parentTemplateTagId)));
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleFilterClick = ({
    type,
    value,
  }: {
    type: typeof TIMESPLIT_FILTER_PARAM | typeof TAG_FILTER_PARAM;
    value: string;
  }) => {
    const newFilters = new Set(type === TIMESPLIT_FILTER_PARAM ? timesplitFilters : tagFilters);
    newFilters.has(value) ? newFilters.delete(value) : newFilters.add(value);
    updateSearchParams(type, Array.from(newFilters));
  };

  const updateSearchParams = (param: typeof TIMESPLIT_FILTER_PARAM | typeof TAG_FILTER_PARAM, values: string[]) => {
    const updatedSearchParams = new URLSearchParams(searchParams);

    updatedSearchParams.delete(param);
    values.forEach((value) => updatedSearchParams.append(param, value));

    setSearchParams(updatedSearchParams);
  };

  const handleSelect = (template: TemplateWithTimesplit) => {
    fireScheduleTemplateSelected({
      timeSplit: template.categoryId,
      pattern: Number(template.scheduleTemplateId),
    });

    const formData = {
      timesplitName: template.timesplitName,
      templateId: template.scheduleTemplateId,
    };

    submit(formData, { method: 'POST' });
  };

  const handleSelectCustom = () => {
    fireScheduleTemplateSelected({ timeSplit: 5, pattern: CUSTOM_TEMPLATE_ID });

    submit({ timesplitName: CUSTOM_TIMESPLIT, templateId: CUSTOM_TEMPLATE_ID }, { method: 'POST' });
  };

  useAnalyticsEvents({ analyticsEvents });

  return (
    <div className="mx-auto my-0 flex max-w-96 flex-col screen-md:max-w-[852px]">
      <Header
        title="Pick a schedule template"
        description={
          <>
            <span className="block">
              Choose how you want to split time with your coparent. Then, select a pattern to customize!
            </span>
            <span className="block">
              <span className="font-bold">Tip:</span> Our examples show schedules beginning on Sunday, but you can start
              most patterns any day of the week.
            </span>
          </>
        }
      />

      <div className="flex flex-col items-center justify-center gap-2 py-4">
        <TimesplitFilters
          availableTimesplits={availableTimesplits}
          onClickTimesplit={(timesplitName) =>
            handleFilterClick({ type: TIMESPLIT_FILTER_PARAM, value: timesplitName })
          }
          tagFilters={tagFilters}
          templateCategories={templateCategories}
          timesplitFilters={timesplitFilters}
        />
      </div>

      <TagFilterMenu
        availableTagIds={availableTagIds}
        isMenuOpen={isMenuOpen}
        onResetFilters={() => setSearchParams({})}
        onTagClick={(tagId) =>
          handleFilterClick({
            type: TAG_FILTER_PARAM,
            value: tagId,
          })
        }
        setIsMenuOpen={setIsMenuOpen}
        tags={tags}
      />

      {!isMenuOpen && tagFilters.length ? (
        <ScrollableTagList
          onRemoveTag={(tagId) => handleFilterClick({ type: TAG_FILTER_PARAM, value: tagId })}
          tagFilters={tagFilters}
          tags={tags}
        />
      ) : null}

      <ul className="flex flex-wrap items-start justify-center gap-x-2 gap-y-6 pt-4">
        {templates.map((template) => {
          return (
            <li
              key={template.scheduleTemplateId}
              className={`w-full screen-md:ml-auto screen-md:mr-auto screen-md:w-[calc(50%-1rem)] ${isLoading ? '' : 'animate-fadeInUp'}`}
            >
              <div className="flex flex-col">
                <TemplateTypeCard onSelect={() => handleSelect(template)} template={template} />
              </div>
            </li>
          );
        })}
        <li
          className={`w-full screen-md:ml-auto screen-md:mr-auto screen-md:w-[calc(50%-1rem)] ${isLoading ? '' : 'animate-fadeInUp'}`}
        >
          <div className="flex flex-col">
            <CustomScheduleCard onSelect={handleSelectCustom} />
          </div>
        </li>
      </ul>

      <Notification notification={notification} />
    </div>
  );
}
